import {
  translateLeagueDisplay,
  translateMarketDisplay,
  translateSelectionDisplay,
  mapFixtureBetToLabel,
  classifySportForAnalysisTab,
  splitMarketPeriodForTranslation,
  extractEmbeddedLineFromRawMarket,
  extractLineFromArbMiddleItemLegs,
  inferImplicitFullTimeScopeArbMiddle,
  isLineBasedMarketForArbMiddle,
  resolveMarketCanonicalKey,
  normalizeLookupKey,
} from "./analysisDataMaps";

import {
  formatAnalysisTime,
  formatAnalysisPrematchDate,
  getAnalysisStatusLabel,
} from "./analysisTimeUtils";

import {
  translateArbMarketLabel,
  getArbDisplayTranslatorBag,
} from "./arbPresentation.shared";

import {
  translateMarketDisplayWithPeriodSplit as translateMarketDisplayWithPeriodSplitShared,
  splitOrdinalEsportsMapSetMarket,
  composeOrdinalEsportsMapSetMarketLabel,
} from "./analysisMarketPresentation.shared";

import {
  ANALYSIS_DEFAULT_LANG,
  ANALYSIS_LANGS,
  analysisTranslations,
  getMissingTranslationKeys,
  validateAllAnalysisTranslations,
  getAnalysisTranslationsData,
  getAnalysisTextData,
} from "./analysisTranslationData.shared";

/*
 * Value-tab presentation logic (Task V2C.2A). Single implementation now
 * lives in ./valuePresentation.shared.js (CommonJS, no dependency on this
 * file) together with the small set of Arb/Middles-shared helpers below.
 * Re-exported / reused here to preserve every existing callsite
 * (ValueBetsPage.jsx, ArbitragePage.jsx, MiddlesPage.jsx) unchanged.
 */
import {
  isMeaningfulLineArbMiddle,
  ARB_MIDDLE_SCOPE_LABELS,
  resolveArbMiddleScopeLabel,
  arbMiddleScopeAlreadyDisplayed,
  arbMiddleLineAlreadyInLabel,
  looksLikeRawSharpTokenString,
  buildValueBetMarketRowLabel,
  buildValueBetSelectionRowLabel,
  buildCanonicalValueBetDisplayLabel,
  canonicalValueBetMarketForTranslation,
} from "./valuePresentation.shared";

export {
  buildValueBetMarketRowLabel,
  buildValueBetSelectionRowLabel,
  buildCanonicalValueBetDisplayLabel,
  canonicalValueBetMarketForTranslation,
};

export {
  ANALYSIS_DEFAULT_LANG,
  ANALYSIS_LANGS,
  analysisTranslations,
  getMissingTranslationKeys,
  validateAllAnalysisTranslations,
};

const __analysisWarnedLangs = new Set();

function warnMissingKeysIfNeeded(lang) {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV === "production") return;
  if (lang === ANALYSIS_DEFAULT_LANG) return;
  if (__analysisWarnedLangs.has(lang)) return;

  const missing = getMissingTranslationKeys(lang);
  if (missing.length) {
    console.warn(
      `[analysisTranslate] Missing ${missing.length} keys for ${lang}. Falling back to ${ANALYSIS_DEFAULT_LANG}.`,
      missing
    );
  }

  __analysisWarnedLangs.add(lang);
}

(function runAnalysisTranslationValidationOnce() {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV === "production") return;

  const report = validateAllAnalysisTranslations();
  const hasAnyMissing = Object.values(report).some((arr) => arr.length > 0);

  if (hasAnyMissing) {
    console.groupCollapsed("[analysisTranslate] Translation key validation");
    Object.entries(report).forEach(([lang, missing]) => {
      if (missing.length) {
        console.warn(`${lang} missing ${missing.length} keys`, missing);
      }
    });
    console.groupEnd();
  }
})();

export function getAnalysisTranslations(lang) {
  const normalizedLang =
    ANALYSIS_LANGS.includes(lang) ? lang : ANALYSIS_DEFAULT_LANG;

  const merged = getAnalysisTranslationsData(lang);
  warnMissingKeysIfNeeded(normalizedLang);

  return merged;
}

export function getAnalysisText(lang, group, key, fallback = "") {
  const normalizedLang =
    ANALYSIS_LANGS.includes(lang) ? lang : ANALYSIS_DEFAULT_LANG;

  const text = getAnalysisTextData(lang, group, key, fallback);
  warnMissingKeysIfNeeded(normalizedLang);

  return text;
}

/**
 * Public UI wrapper: delegates to the pure shared implementation
 * (./analysisMarketPresentation.shared) and preserves the existing
 * development-only missing-translation-key warning behavior. The full
 * market/period translation logic lives only in the shared module.
 */
export function translateMarketDisplayWithPeriodSplit(raw, lang) {
  const normalizedLang =
    ANALYSIS_LANGS.includes(lang) ? lang : ANALYSIS_DEFAULT_LANG;

  const result = translateMarketDisplayWithPeriodSplitShared(raw, lang);
  warnMissingKeysIfNeeded(normalizedLang);

  return result;
}

/*
 * isMeaningfulLineArbMiddle / ARB_MIDDLE_SCOPE_LABELS / resolveArbMiddleScopeLabel /
 * arbMiddleScopeAlreadyDisplayed / arbMiddleLineAlreadyInLabel now live in
 * ./valuePresentation.shared.js (single implementation shared with the Value
 * label builders that also need them). Imported above; used unchanged below.
 */

function mergeArbMiddleItemLineFields(itemLine, lineA, lineB) {
  if (isMeaningfulLineArbMiddle(itemLine)) return String(itemLine).trim();
  const a = lineA == null ? "" : String(lineA).trim();
  const b = lineB == null ? "" : String(lineB).trim();
  if (a && b && a !== b) return `${a} / ${b}`;
  return a || b || "";
}

/**
 * Market row label for Arbitrage + Middles: translated market · scope · line (when line-based).
 */
export function buildArbitrageMiddleMarketRowLabel(item, lang) {
  const rawMarket = String(item?.market || "").trim();
  if (!rawMarket) return "";

  const { line: embeddedLine, remainder: rawNoLine } = extractEmbeddedLineFromRawMarket(rawMarket);
  const mergedItemLine = mergeArbMiddleItemLineFields(
    item?.line ?? item?.point ?? item?.handicap ?? item?.total,
    item?.lineA,
    item?.lineB
  );
  let resolvedLine = "";
  if (isMeaningfulLineArbMiddle(mergedItemLine)) resolvedLine = mergedItemLine;
  else if (isMeaningfulLineArbMiddle(embeddedLine)) resolvedLine = embeddedLine;
  else {
    const legLine = extractLineFromArbMiddleItemLegs(item);
    if (isMeaningfulLineArbMiddle(legLine)) resolvedLine = legLine;
  }

  const sportTab = classifySportForAnalysisTab(item?.sport ?? "", {
    league: item?.league,
    match: item?.match ?? item?.matchName ?? item?.eventName,
  });

  let split = splitMarketPeriodForTranslation(rawNoLine);
  let scopeKey = split.scopeKey;
  const baseRaw = String(split.baseRaw || "").trim() || rawNoLine;

  if (!scopeKey) {
    const inferred = inferImplicitFullTimeScopeArbMiddle(baseRaw, sportTab);
    if (inferred) scopeKey = inferred;
  }

  const canonBase =
    resolveMarketCanonicalKey(baseRaw) ||
    resolveMarketCanonicalKey(rawNoLine) ||
    resolveMarketCanonicalKey(rawMarket) ||
    "";
  let baseTr = "";
  if (canonBase) {
    baseTr = translateMarketDisplay(canonBase, lang);
  }
  if (!baseTr) {
    baseTr =
      translateMarketDisplay(baseRaw, lang) ||
      translateMarketDisplay(rawNoLine, lang) ||
      translateMarketDisplay(rawMarket, lang);
  }

  const ordinal = splitOrdinalEsportsMapSetMarket(baseRaw) || splitOrdinalEsportsMapSetMarket(rawNoLine);
  if (ordinal) {
    const composed = composeOrdinalEsportsMapSetMarketLabel(lang, ordinal);
    if (composed) baseTr = composed;
  }

  const scopeLabel = scopeKey ? resolveArbMiddleScopeLabel(scopeKey) : "";
  let marketPart = baseTr;
  if (scopeLabel && !arbMiddleScopeAlreadyDisplayed(baseTr, scopeLabel)) {
    marketPart = `${baseTr} · ${scopeLabel}`;
  }

  const nkNorm = normalizeLookupKey(
    resolveMarketCanonicalKey(baseRaw) || resolveMarketCanonicalKey(rawNoLine) || baseRaw || ""
  );
  const lineBased =
    isLineBasedMarketForArbMiddle(baseRaw, rawNoLine) ||
    (nkNorm === "corner match bet" && isMeaningfulLineArbMiddle(resolvedLine));
  if (!lineBased) return marketPart;

  if (!resolvedLine) return marketPart;
  if (arbMiddleLineAlreadyInLabel(marketPart, resolvedLine)) return marketPart;
  return `${marketPart} · ${resolvedLine}`;
}

/*
 * buildValueBetMarketRowLabel now lives in ./valuePresentation.shared.js
 * (imported above, re-exported below for the existing ValueBetsPage.jsx /
 * baseline-test import contract).
 */

/**
 * Same market family + period contract as {@link buildValueBetMarketRowLabel}, but ignores free-text
 * `market` / `display_market` so the row stays market-family-first when those fields carry selection-shaped text.
 */
export function buildValueBetMarketFamilyOnlyRowLabel(item, lang) {
  if (!item || typeof item !== "object") return "";
  return buildValueBetMarketRowLabel(
    {
      ...item,
      display_market: "",
      displayMarket: "",
      market: "",
    },
    lang
  );
}

/*
 * buildValueBetSelectionRowLabel / buildCanonicalValueBetDisplayLabel /
 * canonicalValueBetMarketForTranslation now live in
 * ./valuePresentation.shared.js, together with the private helpers they
 * used exclusively (readValueBetLineForSemanticLabel, readSharpMarketTypeSnake,
 * readValueBetTeamsForSemanticLabel, parseValueBetStructuredSideAndOu,
 * inferValueBetSpreadSideAndSelectionRemainder, joinValueBetSemanticParts,
 * stripRepeatedLeadingTeamName, the VALUE_SEMANTIC_* sets, ouWord,
 * oddEvenWord, spreadFamilyLabel). Imported above and re-exported below for
 * the existing ValueBetsPage.jsx / baseline-test import contract.
 *
 * `looksLikeRawSharpTokenString` also moved there (shared with
 * buildValueBetTeamResolvedSelectionLabel below); imported above.
 */

export function getMarketTerm(lang, key) {
  return getAnalysisText(lang, "marketTerms", key);
}

/**
 * Resolves Sharp-style selection tokens (home_over, away under, Home + line) using team names when safe.
 * `display_selection` is a hint only: when `selection_type` / `selection` exist, they define semantics so the
 * Value page can append translated `market_type` without losing the market family.
 */
export function buildValueBetTeamResolvedSelectionLabel(bet, lang) {
  const displaySel = String(bet?.display_selection ?? bet?.displaySelection ?? "").trim();
  const st = String(bet?.selection_type ?? bet?.selectionType ?? "").trim();
  const selPick = String(bet?.selection ?? bet?.pick ?? bet?.outcome ?? "").trim();
  const structuredSel = st || selPick;

  const rawSelection = structuredSel;
  if (!rawSelection) {
    if (displaySel && !looksLikeRawSharpTokenString(displaySel)) {
      return mapFixtureBetToLabel(displaySel, lang) || displaySel;
    }
    return "";
  }

  const readTeamName = (v) => {
    const s = String(v ?? "").trim();
    return s ? s : "";
  };

  const parseTeamsFromMatch = (matchStr) => {
    const m = String(matchStr ?? "").trim();
    if (!m) return { home: "", away: "" };
    const lower = m.toLowerCase();
    const vsIdx = lower.indexOf(" vs ");
    if (vsIdx >= 0) {
      const left = m.slice(0, vsIdx).trim();
      const right = m.slice(vsIdx + 4).trim();
      if (left && right) return { home: left, away: right };
    }
    const vIdx = lower.indexOf(" v ");
    if (vIdx >= 0) {
      const left = m.slice(0, vIdx).trim();
      const right = m.slice(vIdx + 3).trim();
      if (left && right) return { home: left, away: right };
    }
    return { home: "", away: "" };
  };

  const fromMatch = parseTeamsFromMatch(bet?.match ?? bet?.game ?? "");
  const home =
    readTeamName(bet?.home_team ?? bet?.homeTeam ?? bet?.fixture?.teams?.home?.name ?? bet?.fixture?.teams?.home) ||
    fromMatch.home ||
    "";
  const away =
    readTeamName(bet?.away_team ?? bet?.awayTeam ?? bet?.fixture?.teams?.away?.name ?? bet?.fixture?.teams?.away) ||
    fromMatch.away ||
    "";

  const getSelectionLabel = () =>
    mapFixtureBetToLabel(rawSelection, lang) ||
    translateSelectionDisplay(rawSelection, lang) ||
    String(rawSelection ?? "").trim();

  if (!home && !away) return getSelectionLabel();

  const norm = rawSelection
    .toLowerCase()
    .replace(/[\s\-./]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");

  const tokens = norm.split("_").filter(Boolean);
  if (!tokens.length) return getSelectionLabel();

  const isNumericLike = (tok) => {
    const t2 = String(tok ?? "").trim();
    if (!t2) return false;
    return /^[-+]?\d+(\.\d+)?$/.test(t2);
  };

  const overWord =
    translateSelectionDisplay("over", lang) || mapFixtureBetToLabel("over", lang) || "Over";
  const underWord =
    translateSelectionDisplay("under", lang) || mapFixtureBetToLabel("under", lang) || "Under";

  let replacedAny = false;
  let uncertain = false;
  const periodTok = new Set(["ft", "ht", "2h", "1h", "h1", "h2", "ot"]);

  const out = tokens.map((tok) => {
    if (tok === "home" || tok === "h") {
      if (!home) {
        uncertain = true;
        return tok;
      }
      replacedAny = true;
      return home;
    }
    if (tok === "away" || tok === "a") {
      if (!away) {
        uncertain = true;
        return tok;
      }
      replacedAny = true;
      return away;
    }
    if (tok === "over" || tok === "o") {
      replacedAny = true;
      return overWord;
    }
    if (tok === "under" || tok === "u") {
      replacedAny = true;
      return underWord;
    }
    if (isNumericLike(tok)) return tok;
    if (periodTok.has(tok)) {
      replacedAny = true;
      if (tok === "ht" || tok === "h1" || tok === "1h") return "HT";
      if (tok === "2h" || tok === "h2") return "2H";
      if (tok === "ft") return "FT";
      if (tok === "ot") return "OT";
      return tok.toUpperCase();
    }

    uncertain = true;
    return tok;
  });

  if (!replacedAny || uncertain) return getSelectionLabel();

  const resolved = out.join(" ").replace(/\s+/g, " ").trim();
  if (!resolved) return getSelectionLabel();

  return resolved;
}

export function normalizeSteamFlagKey(rawFlag) {
  const s = String(rawFlag ?? "").trim();
  if (!s) return "";
  const lower = s.replace(/\s+/g, " ").trim().toLowerCase();
  if (lower === "ev+" || lower === "ev +") return "evPlus";
  if (lower === "ev") return "ev";
  if (lower === "pinnacle") return "pinnacle";
  if (lower === "movement") return "movement";
  if (lower === "splits") return "splits";
  if (lower === "betting splits") return "splits";
  return lower;
}

export function getSteamTierLabel(lang, tier) {
  const t = String(tier ?? "").toLowerCase();
  if (t === "green") return getAnalysisText(lang, "steam", "tierGreen");
  if (t === "orange") return getAnalysisText(lang, "steam", "tierOrange");
  if (t === "yellow") return getAnalysisText(lang, "steam", "tierYellow");
  return "";
}

export function getSteamSignalLabel(lang, tier) {
  const t = String(tier ?? "").toLowerCase();
  if (t === "green") return getAnalysisText(lang, "steam", "signalLabelGreen");
  if (t === "orange") return getAnalysisText(lang, "steam", "signalLabelOrange");
  if (t === "yellow") return getAnalysisText(lang, "steam", "signalLabelYellow");
  return "";
}

export function translateSteamFlag(lang, rawFlag) {
  const nk = normalizeSteamFlagKey(rawFlag);
  const map = {
    splits: "chipBettingSplits",
    movement: "chipMovement",
    pinnacle: "chipPinnacle",
    ev: "chipEv",
    evPlus: "chipEvPositive",
  };
  const steamKey = map[nk];
  if (steamKey) return getAnalysisText(lang, "steam", steamKey);
  return String(rawFlag ?? "").trim();
}

export function getSteamSignalReason(lang, bet) {
  let tier = String(bet?.signalTier ?? bet?.steamTier ?? bet?.severity ?? "").toLowerCase();
  if (!tier || tier === "unknown") {
    const hasM = bet?.hasMovement === true;
    const hasP = bet?.hasPinnacleConfirm === true;
    const hasEv = bet?.hasPositiveEv === true;
    if (hasM && hasP && hasEv) tier = "green";
    else if (hasM) tier = "orange";
    else tier = "yellow";
  }
  if (tier === "green") return getAnalysisText(lang, "steam", "reasonGreen");
  if (tier === "orange") return getAnalysisText(lang, "steam", "reasonOrange");
  if (tier === "yellow") return getAnalysisText(lang, "steam", "reasonYellow");
  return "";
}

export function getSteamMetaLabels(lang) {
  return {
    splits: getAnalysisText(lang, "steam", "splitsLabel"),
    bets: getAnalysisText(lang, "steam", "betsLabel"),
    splitsGap: getAnalysisText(lang, "steam", "splitsGapLabel"),
    sharpDevig: getAnalysisText(lang, "steam", "sharpDevigLabel"),
    ev: getAnalysisText(lang, "steam", "evLabel"),
    movement: getAnalysisText(lang, "steam", "movementLabel"),
    bettingSplits: getAnalysisText(lang, "steam", "bettingSplitsLabel"),
    movementConfirmed: getAnalysisText(lang, "steam", "movementConfirmedLabel"),
    pinnacleConfirmed: getAnalysisText(lang, "steam", "pinnacleConfirmedLabel"),
    positiveEv: getAnalysisText(lang, "steam", "positiveEvLabel"),
    preview: getAnalysisText(lang, "steam", "previewLabel"),
  };
}

/**
 * UI-only odds formatter for Analysis tabs.
 * HK display odds = decimalOdds - 1 (does not mutate any data objects).
 *
 * @param {unknown} decimalOdds
 * @param {"hk"} mode
 * @returns {string|null}
 */
export function formatAnalysisOddsDisplay(decimalOdds, mode = "hk") {
  const n = Number(decimalOdds);
  if (!Number.isFinite(n) || n <= 0) return null;

  const m = String(mode || "hk").toLowerCase().trim();

  if (m === "decimal") {
    if (n <= 1) return null;
    const fixed = n.toFixed(3);
    return fixed.replace(/(?:\.0+|(\.\d*?)0+)$/, "$1");
  }

  if (m === "american") {
    if (n <= 1) return null;
    const american = n >= 2
      ? Math.round((n - 1) * 100)
      : Math.round(-100 / (n - 1));
    if (!Number.isFinite(american) || american === 0) return null;
    return american > 0 ? `+${american}` : String(american);
  }

  // HK (default)
  if (n <= 1) return null;
  const hk = n - 1;
  if (!Number.isFinite(hk)) return null;
  const fixed = hk.toFixed(3);
  return fixed.replace(/(?:\.0+|(\.\d*?)0+)$/, "$1");
}

/*
 * Arb presentation translation (market label overrides + translator bag)
 * now lives in ./arbPresentation.shared.js (CommonJS, shared with
 * backend/Telegram callers). Re-exported below to preserve the existing
 * UI import contract for ArbitragePage.jsx and any other call sites.
 */
export { translateArbMarketLabel, getArbDisplayTranslatorBag };

/**
 * Value-tab market family translator (Task G).
 *
 * Maps SharpAPI structured `market_type` keys to the canonical Vietnamese /
 * English family label used by the Value card header. Returns "" when the key
 * is unrecognised so callers can fall back to the existing translation chain
 * (`translateMarketDisplay` etc.).
 *
 * The map is intentionally narrow: it only covers the keys explicitly listed
 * in the Value display contract so other tabs (Arb / Middles / Steam) keep
 * their existing translations untouched.
 */
const VALUE_MARKET_FAMILY_TRANSLATIONS_VI = {
  point_spread: "Cược Chấp",
  spread: "Cược Chấp",
  handicap: "Cược Chấp",
  asian_handicap: "Cược Chấp Châu Á",
  european_handicap: "Cược Chấp Châu Âu",
  game_spread: "Cược Chấp",
  game_handicap: "Cược Chấp",
  set_handicap: "Cược Chấp Set",
  run_line: "Cược Chấp",
  puck_line: "Cược Chấp",
  corner_handicap: "Cược Chấp Phạt Góc",
  corners_handicap: "Cược Chấp Phạt Góc",
  asian_handicap_corners: "Cược Chấp Góc Châu Á",
  corners_asian_handicap: "Cược Chấp Góc Châu Á",
  total_points: "Tài/Xỉu",
  total_goals: "Tài/Xỉu bàn thắng",
  total_runs: "Tài/Xỉu runs",
  total_games: "Tài/Xỉu games",
  total_sets: "Tài/Xỉu sets",
  total_corners: "Tài/Xỉu phạt góc",
  total: "Tài/Xỉu",
  totals: "Tài/Xỉu",
  over_under: "Tài/Xỉu",
  team_total: "Tổng đội",
  team_total_goals: "Tổng bàn thắng đội",
  team_total_points: "Tổng điểm đội",
  team_total_runs: "Tổng runs đội",
  team_total_corners: "Tổng phạt góc đội",
  "1st_half_team_total": "Tổng Đội · HT",
  "1st_half_team_total_goals": "Tổng bàn thắng đội HT",
  "1st_half_team_total_points": "Tổng điểm đội HT",
  "2nd_half_team_total": "Tổng Đội · 2H",
  "2nd_half_team_total_goals": "Tổng bàn thắng đội 2H",
  "2nd_half_team_total_points": "Tổng điểm đội 2H",
  moneyline: "Kèo Thắng",
  match_winner: "Kèo Thắng",
  winner: "Kèo Thắng",
  h2h: "Kèo Thắng",
  draw_no_bet: "Kèo Thắng (Hoàn tiền hòa)",
  "1x2": "Kèo thắng 3 cửa",
  moneyline_3_way: "Kèo thắng 3 cửa",
};

const VALUE_MARKET_FAMILY_TRANSLATIONS_EN = {
  point_spread: "Point Spread",
  spread: "Spread",
  handicap: "Handicap",
  asian_handicap: "Asian Handicap",
  european_handicap: "European Handicap",
  game_spread: "Game Spread",
  game_handicap: "Game Handicap",
  set_handicap: "Set Handicap",
  run_line: "Run Line",
  puck_line: "Puck Line",
  corner_handicap: "Corners Handicap",
  corners_handicap: "Corners Handicap",
  asian_handicap_corners: "Asian Handicap Corners",
  corners_asian_handicap: "Asian Handicap Corners",
  total_points: "Total Points",
  total_goals: "Total Goals",
  total_runs: "Total Runs",
  total_games: "Total Games",
  total_sets: "Total Sets",
  total_corners: "Total Corners",
  total: "Total",
  totals: "Totals",
  over_under: "Over/Under",
  team_total: "Team Total",
  team_total_goals: "Team Total Goals",
  team_total_points: "Team Total Points",
  team_total_runs: "Team Total Runs",
  team_total_corners: "Team Total Corners",
  "1st_half_team_total": "Team Total HT",
  "1st_half_team_total_goals": "Team Total Goals HT",
  "1st_half_team_total_points": "Team Total Points HT",
  "2nd_half_team_total": "Team Total 2H",
  "2nd_half_team_total_goals": "Team Total Goals 2H",
  "2nd_half_team_total_points": "Team Total Points 2H",
  moneyline: "Moneyline",
  match_winner: "Match Winner",
  winner: "Winner",
  h2h: "Moneyline",
  draw_no_bet: "Draw No Bet",
  "1x2": "1X2",
  moneyline_3_way: "3-Way Moneyline",
};

function _normalizeValueMarketFamilyKey(rawKey) {
  return String(rawKey ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s./-]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Translate a SharpAPI structured market_type key into a Value-tab family
 * label. Returns "" for unknown keys so callers can fall back.
 *
 * Used only by the Value card header / copy text — the existing arb / middles
 * translation chain is unaffected.
 */
export function translateValueMarketFamilyKey(rawMarketTypeKey, lang) {
  const key = _normalizeValueMarketFamilyKey(rawMarketTypeKey);
  if (!key) return "";
  const lng = String(lang || "").toLowerCase();
  const bucket = lng.startsWith("vi")
    ? VALUE_MARKET_FAMILY_TRANSLATIONS_VI
    : VALUE_MARKET_FAMILY_TRANSLATIONS_EN;
  if (Object.prototype.hasOwnProperty.call(bucket, key)) return bucket[key];
  // Tolerate common synonyms not present in the explicit map.
  if (key === "money_line") return bucket.moneyline;
  if (key === "moneyline_3way") return bucket.moneyline_3_way;
  return "";
}

export {
  translateLeagueDisplay,
  translateMarketDisplay,
  translateSelectionDisplay,
  mapFixtureBetToLabel,
  classifySportForAnalysisTab,
  formatAnalysisTime,
  formatAnalysisPrematchDate,
  getAnalysisStatusLabel
};